/** Hosted-demo empty state and graph-first starter tasks. */

import type { JSX } from "react";

import magnisLogoRoundedUrl from "../../../desktop/icon-rounded.png";
import { useAnalytics } from "../analytics/provider";
import type { DemoDatasetSummary } from "../hooks/useDemoSeedStatus";

const PRESETS: readonly {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly prompt: string;
}[] = [
  {
    id: "missed_commitments",
    label: "Find missed commitments",
    description: "Owners, deadlines, sources, next actions",
    prompt:
      "Find commitments that are overdue or at risk across my email, chats, meetings, and notes. Create a note titled ‘Missed commitments’ with the owner, deadline, source conversation, and next action for each one.",
  },
  {
    id: "meridian_blockers",
    label: "What blocks Meridian?",
    description: "Follow one story across the graph",
    prompt:
      "Trace what blocks the Meridian pilot and where each blocker was discussed. Create a note titled ‘Meridian unblock plan’ with the evidence, owner, and next action for every blocker.",
  },
  {
    id: "unanswered_people",
    label: "Who never got a reply?",
    description: "Per person, not per account",
    prompt:
      "Find every person in this workspace who sent a message or email and never received a reply. Create a note titled ‘Unanswered follow-ups’, grouped by person, with the unanswered source and recommended follow-up.",
  },
  {
    id: "urbangrid_brief",
    label: "Prepare an UrbanGrid brief",
    description: "People, conversations, risks, decisions",
    prompt:
      "Create a note titled ‘UrbanGrid account brief’ covering key people, recent conversations, open risks, decisions, and next actions. Cite the source records.",
  },
];

interface DemoPresetsProps {
  readonly summary: DemoDatasetSummary | null;
  readonly onSelect: (prompt: string) => void;
}

export function DemoPresets({
  summary,
  onSelect,
}: DemoPresetsProps): JSX.Element | null {
  const analytics = useAnalytics();
  if (analytics.runtimeSurface !== "hosted_demo" || summary === null) {
    return null;
  }

  return (
    <section
      data-testid="hosted-demo-starter-prompts"
      aria-labelledby="hosted-demo-empty-title"
      className="mx-auto flex w-full max-w-[720px] flex-col items-center px-4 text-center"
    >
      <img
        src={magnisLogoRoundedUrl}
        alt="Magnis"
        className="h-8 w-8 rounded-lg"
      />
      <h1
        id="hosted-demo-empty-title"
        className="mt-3 text-base font-medium text-agent-text"
      >
        {summary.companyName} is loaded. Try something hard.
      </h1>
      <p className="mt-1 text-[13px] text-agent-text-dim">
        {summary.people.toLocaleString()} people, {summary.companies.toLocaleString()} companies, {summary.messages.toLocaleString()} messages — all linked.
      </p>
      <div className="mt-5 grid w-full grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-2.5">
        {PRESETS.map((preset) => (
          <button
            key={preset.id}
            type="button"
            aria-label={preset.label}
            className="rounded-xl border border-agent-border bg-surface-secondary px-4 py-3 text-left transition-colors hover:border-accent/60 hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
            onClick={(): void => {
              if (analytics.mode !== "off") {
                void analytics.capture(
                  "demo_case_viewed",
                  {
                    case_id: preset.id,
                    case_label: preset.label,
                  },
                  { source: "demo_presets" },
                ).catch((error: unknown) => {
                  // eslint-disable-next-line no-console
                  console.error("analytics capture failed", error);
                });
              }
              onSelect(preset.prompt);
            }}
          >
            <span className="block text-sm font-medium text-agent-text">
              {preset.label}
            </span>
            <span className="mt-1 block text-xs leading-4 text-agent-text-dim">
              {preset.description}
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}
